import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation } from "@apollo/client";
import { Modal, Button, Stack, Text, List } from "@mantine/core";
import { useWaveConfig, consumeDisclosureShown } from "../WaveConfigContext";
import { ACKNOWLEDGE_ARM_A_DISCLOSURE } from "../../queries/waves";

/* ============================================================================
 * Wave E — Obstruction, guide §5a.
 *
 * A one-time-per-session modal shown to Phase 3 Arm A participants BEFORE they
 * trade, explaining that every position close goes through the 4-step flow.
 * `consumeDisclosureShown` records it in sessionStorage (keyed by wave_id) so
 * it appears at most once per session. Renders nothing for anyone else.
 * ==========================================================================*/

export default function ArmADisclosureModal() {
    const { config } = useWaveConfig();
    const [opened, setOpened] = useState(false);
    const evaluatedForWave = useRef(null);
    const ackedRef = useRef(false);

    const [acknowledge] = useMutation(ACKNOWLEDGE_ARM_A_DISCLOSURE);

    useEffect(() => {
        if (!config) return;
        // Evaluate once per wave per mount — consumeDisclosureShown has a side
        // effect, so guard against re-running it on unrelated re-renders.
        if (evaluatedForWave.current === config.waveId) return;
        evaluatedForWave.current = config.waveId;
        if (consumeDisclosureShown(config)) {
            ackedRef.current = false;
            setOpened(true);
        }
    }, [config]);

    // Stamp arm_a_disclosure_shown_at once when the participant dismisses the
    // modal ("Got it" or the close-X). Idempotent server-side; fire-and-forget
    // so it never blocks the UI. Also logs a max_modal_response event (§5o).
    const dismiss = useCallback(() => {
        if (!ackedRef.current) {
            ackedRef.current = true;
            acknowledge().catch(() => {});
        }
        setOpened(false);
    }, [acknowledge]);

    return (
        <Modal
            opened={opened}
            onClose={dismiss}
            title="Before you trade this session"
            centered
            closeOnClickOutside={false}
        >
            <Stack>
                <Text size="sm">When you close a position, you will go through 4 steps:</Text>
                <List type="ordered" size="sm" spacing="xs">
                    <List.Item>Review your current P&L</List.Item>
                    <List.Item>Acknowledge market risk</List.Item>
                    <List.Item>Confirm exit price and quantity</List.Item>
                    <List.Item>Wait 10 seconds before the submit button is enabled</List.Item>
                </List>
                <Text size="sm" color="dimmed">This applies to every position close this session.</Text>
                <Button onClick={dismiss} mt="sm">Got it</Button>
            </Stack>
        </Modal>
    );
}
